import axios, { type AxiosError } from "axios";
import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import { ApiHttpError, handleAxiosError } from "@/lib/apiErrors";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

const handleTopicsApiError = (error: unknown, label: string): never => {
  if (isAxiosError(error)) {
    if (!error.response) {
      throw new Error("Please check your connection");
    }
    console.error(`${label} API Error:`, error.response.data);
    throw error;
  }
  throw new Error("Unexpected error occurred");
};

export interface TopicChannelResponse {
  id?: number | string;
  name?: string;
  description?: string | null;
  is_archived_channel?: boolean;
  is_admin_only_channel?: boolean;
}

export interface TopicChannelsListResponse {
  total: number;
  page: number;
  pages: number;
  links: {
    self: string;
    first: string;
    last: string;
  };
  results: TopicChannelResponse[];
}

export interface ChannelUnreadCount {
  channel_key: string;
  channel_id: number | null;
  channel_name: string;
  unread_post_comment_count: number;
  unread_mention_count: number;
}

export interface ChannelUnreadCountsResponse {
  total: number;
  page: number;
  pages: number;
  links: {
    self: string;
    first: string;
    last: string;
    next?: string;
    prev?: string;
  };
  results: ChannelUnreadCount[];
}

export type ChannelVisitPayload = (
  | { channel_key: "home" }
  | { channel_id: number }
) & {
  visited_at?: string;
  snapshot_sequence?: number;
};

export const topicsApi = {
  getChannels: async (
    spaceId: number | string,
    filters?: {
      is_admin_only_channel?: boolean;
      is_archived_channel?: boolean;
    },
  ): Promise<TopicChannelsListResponse> => {
    try {
      const params = {
        ...(typeof filters?.is_admin_only_channel === "boolean"
          ? { is_admin_only_channel: filters.is_admin_only_channel }
          : {}),
        ...(typeof filters?.is_archived_channel === "boolean"
          ? { is_archived_channel: filters.is_archived_channel }
          : {}),
      };
      const response = await api.get<TopicChannelsListResponse>(
        `${API_URLS.TOPIC}/channels/space/${spaceId}`,
        { params },
      );
      return response.data;
    } catch (error: unknown) {
      throw handleTopicsApiError(error, "Get Topic Channels");
    }
  },

  getChannelUnreadCounts: async (
    spaceId: number | string,
  ): Promise<ChannelUnreadCountsResponse> => {
    try {
      const response = await api.get<ChannelUnreadCountsResponse>(
        `${API_URLS.STUDY_GROUP}/${spaceId}/channel-unread-counts`,
      );
      const firstPage = response.data;
      const totalPages = Math.max(firstPage.pages ?? 1, 1);

      if (totalPages <= 1) {
        return firstPage;
      }

      const restResponses = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) =>
          api.get<ChannelUnreadCountsResponse>(
            `${API_URLS.STUDY_GROUP}/${spaceId}/channel-unread-counts`,
            {
              params: { page: index + 2 },
            },
          ),
        ),
      );

      const mergedResults = [
        ...(firstPage.results ?? []),
        ...restResponses.flatMap((rest) => rest.data.results ?? []),
      ];
      const dedupedResults = Array.from(
        new Map(mergedResults.map((item) => [item.channel_key, item])).values(),
      );

      return {
        ...firstPage,
        page: 1,
        pages: 1,
        total: dedupedResults.length,
        results: dedupedResults,
      };
    } catch (error: unknown) {
      throw handleTopicsApiError(error, "Get Channel Unread Counts");
    }
  },

  searchChannels: async (
    spaceId: number | string,
    keyword: string,
    page = 1,
    perPage = 20,
  ): Promise<TopicChannelsListResponse> => {
    const normalizedKeyword = keyword.trim();
    if (!normalizedKeyword) {
      throw new Error("keyword is required");
    }

    try {
      const response = await api.get<TopicChannelsListResponse>(
        `${API_URLS.TOPIC}/channels/space/${spaceId}/search`,
        {
          params: {
            keyword: normalizedKeyword,
            page,
            "per-page": perPage,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw handleTopicsApiError(error, "Search Topic Channels");
    }
  },

  updateChannelVisit: async (
    spaceId: number | string,
    payload: ChannelVisitPayload,
  ): Promise<void> => {
    try {
      await api.put(
        `${API_URLS.STUDY_GROUP}/${spaceId}/membership/channel-visit`,
        payload,
      );
    } catch (error: unknown) {
      throw handleTopicsApiError(error, "Update Channel Visit");
    }
  },

  createChannel: async (
    spaceId: number | string,
    name: string,
    description?: string,
    isAdminOnlyChannel?: boolean,
  ): Promise<TopicChannelResponse> => {
    try {
      const response = await api.post<TopicChannelResponse>(
        `${API_URLS.TOPIC}/channels/space/${spaceId}`,
        {
          name,
          ...(description ? { description } : {}),
          ...(isAdminOnlyChannel ? { is_admin_only_channel: true } : {}),
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(error, "Failed to create channel");
      }
      throw handleTopicsApiError(error, "Create Topic Channel");
    }
  },

  updateChannel: async (
    spaceId: number | string,
    topicId: number | string,
    params: {
      name?: string;
      description?: string;
    },
  ): Promise<TopicChannelResponse> => {
    try {
      const response = await api.put<TopicChannelResponse>(
        `${API_URLS.TOPIC}/channels/space/${spaceId}/channel/${topicId}`,
        {
          ...(typeof params.name === "string" ? { name: params.name } : {}),
          ...(typeof params.description === "string"
            ? { description: params.description }
            : {}),
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(error, "Failed to update channel");
      }
      throw handleTopicsApiError(error, "Update Topic Channel");
    }
  },

  updateChannelArchive: async (
    spaceId: number | string,
    topicId: number | string,
    isArchivedChannel: boolean,
  ): Promise<TopicChannelResponse> => {
    try {
      const response = await api.put<TopicChannelResponse>(
        `${API_URLS.TOPIC}/channels/space/${spaceId}/channel/${topicId}/archive`,
        {
          is_archived_channel: isArchivedChannel,
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw handleTopicsApiError(error, "Update Topic Channel Archive");
    }
  },

  deleteChannel: async (
    spaceId: number | string,
    topicId: number | string,
  ): Promise<void> => {
    try {
      const response = await api.delete<{
        code?: number | string;
        message?: string;
      }>(`${API_URLS.TOPIC}/channels/space/${spaceId}/channel/${topicId}`);
      const responseCode = Number(response.data?.code);
      if (Number.isFinite(responseCode) && responseCode >= 400) {
        throw new ApiHttpError(
          response.data?.message || "Failed to delete channel",
          responseCode,
        );
      }
    } catch (error: unknown) {
      if (error instanceof ApiHttpError) {
        throw error;
      }
      if (isAxiosError(error) && error.response) {
        const payload = error.response.data as
          | { code?: number | string; message?: string }
          | undefined;
        const payloadCode = Number(payload?.code);
        throw new ApiHttpError(
          payload?.message || "Failed to delete channel",
          Number.isFinite(payloadCode) ? payloadCode : error.response.status,
        );
      }
      throw handleTopicsApiError(error, "Delete Topic Channel");
    }
  },
};
